const BACKEND_URI = "";

import {
    ChatAppResponse,
    ChatAppResponseOrError,
    ChatAppRequest,
    Config,
    LikeDislikeRequest,
    FeedbackRequest,
    Chat,
    NewVerifiedQuestion,
    DomainOption
} from "./models";
import { useLogin, appServicesToken } from "../authConfig";

export function getHeaders(idToken: string | undefined): Record<string, string> {
    let headers: Record<string, string> = {
        "Content-Type": "application/json"
    };
    // If using login and not using app services, add the id token of the logged in account as the authorization
    if (useLogin && appServicesToken == null) {
        if (idToken) {
            headers["Authorization"] = `Bearer ${idToken}`;
        }
    }

    return headers;
}

// TODO: possibly removable since we don't have an /ask endpoint in the backend anymore
export async function askApi(request: ChatAppRequest, idToken: string | undefined): Promise<ChatAppResponse> {
    const response = await fetch(`${BACKEND_URI}/ask`, {
        method: "POST",
        headers: getHeaders(idToken),
        body: JSON.stringify(request)
    });

    const parsedResponse: ChatAppResponseOrError = await response.json();
    if (response.status > 299 || !response.ok) {
        throw Error(parsedResponse.error || "Unknown error");
    }

    return parsedResponse as ChatAppResponse;
}

export async function configApi(idToken: string | undefined): Promise<Config> {
    const response = await fetch(`${BACKEND_URI}/config`, {
        method: "GET",
        headers: getHeaders(idToken)
    });

    return (await response.json()) as Config;
}

export async function chatApi(request: ChatAppRequest, idToken: string | undefined): Promise<Response> {
    return await fetch(`${BACKEND_URI}/chat`, {
        method: "POST",
        headers: getHeaders(idToken),
        body: JSON.stringify(request)
    });
}

export async function provideFeedbackApi(request: FeedbackRequest, idToken: string | undefined): Promise<Response> {
    // concatentate the procedure documents into a single string
    return await fetch(`${BACKEND_URI}/feedback`, {
        method: "POST",
        headers: getHeaders(idToken),
        body: JSON.stringify(request)
    });
}

export async function provideLikeDislikeApi(request: LikeDislikeRequest, idToken: string | undefined): Promise<Response> {
    return await fetch(`${BACKEND_URI}/like_dislike`, {
        method: "POST",
        headers: getHeaders(idToken),
        body: JSON.stringify(request)
    });
}

export async function getSourceDocuments(idToken: string | undefined): Promise<Response> {
    return await fetch(`${BACKEND_URI}/source_documents`, {
        method: "GET",
        headers: getHeaders(idToken)
    });
}

export async function getChatSession(idToken: string | undefined): Promise<Response> {
    return await fetch(`${BACKEND_URI}/chat_session`, {
        method: "GET",
        headers: getHeaders(idToken)
    });
}

export function getCitationFilePath(citation: string): string {
    return `${BACKEND_URI}/content/${citation}`;
}

export async function getChats(idToken: string | undefined, page: number = 1, size: number = 20): Promise<Response> {
    return await fetch(`${BACKEND_URI}/chats?page=${page}&size=${size}`, {
        method: "GET",
        headers: getHeaders(idToken)
    });
}

export async function getUploadedDocs(idToken: string | undefined, page: number = 1, size: number = 20): Promise<Response> {
    return await fetch(`${BACKEND_URI}/list_uploaded?page=${page}&size=${size}`, {
        method: "GET",
        headers: getHeaders(idToken)
    })
}

export async function getChat(idToken: string | undefined, chatId: string): Promise<Response> {
    return await fetch(`${BACKEND_URI}/chat/${chatId}`, {
        method: "GET",
        headers: getHeaders(idToken)
    });
}

export async function deleteChat(idToken: string | undefined, chatId: string): Promise<Response> {
    return await fetch(`${BACKEND_URI}/chat/${chatId}`, {
        method: "DELETE",
        headers: getHeaders(idToken)
    });
}

export async function deleteDocument(idToken: string | undefined, files: string[]): Promise<Response> {
    return await fetch(`${BACKEND_URI}/delete_uploaded`, {
        method: "DELETE",
        headers: getHeaders(idToken),
        body: JSON.stringify({ filenames: files })
    })
}

export async function updateChat(idToken: string | undefined, chatId: string, chat: Partial<Chat>): Promise<Response> {
    return await fetch(`${BACKEND_URI}/chat/${chatId}`, {
        method: "PUT",
        headers: getHeaders(idToken),
        body: JSON.stringify(chat)
    });
}

export async function addVerifiedQuestion(idToken: string | undefined, newVerifiedQuestion: NewVerifiedQuestion): Promise<Response> {
    return await fetch(`${BACKEND_URI}/verified_question`, {
        method: "POST",
        headers: getHeaders(idToken),
        body: JSON.stringify(newVerifiedQuestion)
    });
}

export async function getSimilarQuestions(idToken: string | undefined, question: string, selectedDomain: string): Promise<Response> {
    return await fetch(`${BACKEND_URI}/similar_questions?question=${question}&selectedDomain=${encodeURIComponent(selectedDomain)}`, {
        method: "GET",
        headers: getHeaders(idToken)
    });
}

export async function uploadVerifiedQuestionFile(idToken: string | undefined, file: File): Promise<Response> {
    const formData = new FormData();
    formData.append("file", file);
    return await fetch(`${BACKEND_URI}/upload_verified_questions`, {
        method: "POST",
        headers: {
            Authorization: `Bearer ${idToken}`
        },
        body: formData
    });
}

export async function uploadDocumentFiles(idToken: string | undefined, files: File[]): Promise<Response> {
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
        formData.append("file", files[i]);
    }

    return await fetch(`${BACKEND_URI}/upload`, {
        method: "POST",
        headers: {
            Authorization: `Bearer ${idToken}`
        },
        body: formData
    });
}

export async function getDomainList(idToken: string | undefined): Promise<Response> {
    return await fetch(`${BACKEND_URI}/domains`, {
        method: "GET",
        headers: getHeaders(idToken)
    });
}

/* stub for dynamically adding domains
export async function addDomain(idToken: string | undefined, domainName: string): Promise<Response> {
    return await fetch(`${BACKEND_URI}/domains`, {
        method: "POST",
        headers: getHeaders(idToken),
        body: JSON.stringify({ domain_name: domainName })
    });
}*/

export async function updateDomain(idToken: string | undefined, domainId: string, domain: Partial<DomainOption>): Promise<Response> {
    return await fetch(`${BACKEND_URI}/domains/${domainId}`, {
        method: "PUT",
        headers: getHeaders(idToken),
        body: JSON.stringify(domain)
    });
}
